<template>
  <section
    class="section"
    @click="resetExpansions"
  >
    <h1>{{ $t('orderList.heading') }}</h1>
    <form
      @submit.prevent="search"
      class="search"
    >
      <SearchInput
        :label="$t('orderList.searchLabel')"
        :value="searchText"
        @input="searchInput"
      />
      <TextIconButton
        :click="search"
        :label="$t('orderList.searchButton')"
        :icon="'magnifying-glass'"
        :disabled="false"
        :sizeBig="true"
      />
    </form>
    <div class="resultsAndSort">
      <div class="listDetails">
        <div
          v-if="searchTerm.length > 0"
          class="searchTerm"
        >
          {{ $t('orderList.searchTerm') }}:
          <button
            type="button"
            @click="deleteSearchTerm"
          >
            {{ searchTerm }}
            <font-awesome-icon icon="xmark"/>
          </button>
        </div>
        {{ $t('orderList.searchResults') }}: {{ ordersMeta.total }}
      </div>
      <div class="sort">
        <span>{{ $t('orderList.sortBy') }}</span>
        <button
          type="button"
          :class="{ active: sortOrder === '-' }"
          @click="() => sort('-')"
        >
          <span>{{ $t('orderList.sortByDateDescending') }}</span>
          <font-awesome-icon icon="arrow-down-9-1"/>
        </button>
        <button
          type="button"
          :class="{ active: sortOrder === '+' }"
          @click="() => sort('+')"
        >
          <span>{{ $t('orderList.sortByDateAscending') }}</span>
          <font-awesome-icon icon="arrow-down-1-9"/>
        </button>
      </div>
    </div>
    <ul class="list">
      <li
        v-for="order in orders"
        :key="order.id"
      >
        <Order
          :order="order"
          :clients="clients"
          :users="users"
          :user="user"
          @update-order="updateOrder"
          @reset-expansions="resetExpansions"
        />
      </li>
    </ul>
    <Pagination
      :currentPage="ordersMeta.currentPage"
      :lastPage="ordersMeta.lastPage"
      :from="ordersMeta.from"
      :to="ordersMeta.to"
      :total="ordersMeta.total"
      :goToPage="goToPage"
    />
  </section>
</template>

<style scoped>
  .section {
    margin: 100px 0;
  }

  .resultsAndSort {
    display: flex;
    flex-wrap: wrap;
  }

  .sort {
    margin-left: auto;
  }

  .sort > button {
    margin-left: 10px;
    color: var(--primary-color);
    background-color: transparent;
    border: none;
    cursor: pointer;
    transition-property: color;
    transition-duration: 0.2s;
  }

  .sort > button:hover {
    color: var(--secondary-color);
  }

  .sort > button > svg {
    margin-left: 5px;
  }

  .sort > button.active > span {
    padding-bottom: 2px;
    border-bottom: 1px solid transparent;
    transition-property: border-color;
    transition-duration: 0.2s;
  }

  .sort > button.active > span {
    border-bottom-color: var(--primary-color);
  }

  .search {
    display: flex;
    margin: 50px 0;
    align-items: center;
  }

  .search > div:nth-child(1) {
    flex: 1;
  }

  .searchTerm {
    margin-bottom: 20px;
  }

  .searchTerm > button {
    margin-left: 10px;
    color: var(--primary-color);
    background-color: transparent;
    border: none;
    cursor: pointer;
    transition-property: color;
    transition-duration: 0.2s;
  }
  
  .searchTerm > button:hover {
    color: var(--secondary-color);
  }

  .categoryWrapper {
    margin: 20px 20px 0 20px;
  }

  .category {
    font-weight: 600;
  }

  .subCategory {
    font-weight: 600;
  }

  .list {
    margin: 20px 0 50px 0;
    padding: 0;
    list-style-type: none;
  }

  .list > li {
    margin: 0 0 20px 0;
    padding: 0;
    font-size: 1.3rem;
    background-color: var(--secondary-background-color);
    border: 2px solid #e9e9e9;
    border-radius: 5px;
    box-shadow: 6px 6px 22px -15px rgba(130, 130, 130, 1);
  }
</style>

<script>
  import Order from '../order/Order.vue';
  import SearchInput from '../form/SearchInput.vue';
  import TextIconButton from '../buttons/TextIconButton.vue';
  import Pagination from '../pagination/Pagination.vue';

  export default {
    components: {
      Order,
      SearchInput,
      TextIconButton,
      Pagination,
    },
    props: [ 'scope', 'user' ],
    data() {
      return {
        filterTerm: '',
        searchText: '',
        searchTerm: '',
        sortOrder: '-',
        orders: [],
        ordersMeta: {},
        users: [],
        clients: []
      };
    },
    beforeCreate: async function() {
      const data = await Promise.all([
        fetch('/api/user'),
        fetch('/api/client')
      ]);
      const json = await Promise.all(data.map(response => response.json()));
      this.users = json[0];
      this.clients = json[1];

      if (this.scope === 'unassigned') {
        this.filterTerm = '&assigned_user_id=%00';
      } else if (this.scope === 'awaiting_acceptance') {
        this.filterTerm = '&status=awaiting_acceptance';
      } else if (this.scope === 'user') {
        if (['abg_manager', 'abg_user'].includes(this.user.role)) {
          this.filterTerm = `&assigned_user_id=${this.user.id}`;
        } else if (['client_manager', 'client_user'].includes(this.user.role)) {
          this.filterTerm = `&principal_id=${this.user.id}`;
        }
      }

      this.fetchOrderData(this.searchTerm, this.filterTerm, this.sortOrder, '1');
    },
    methods: {
      fetchOrderData: async function (searchTerm, filterTerm, sortOrder, page) {
        const orderData = await fetch(`/api/order?search=${encodeURIComponent(searchTerm)}${filterTerm}&sort=${sortOrder}id&page=${page}`);
        const orderJson = await orderData.json();

        this.ordersMeta = {
          currentPage: orderJson.current_page,
          lastPage: orderJson.last_page,
          from: orderJson.from,
          to: orderJson.to,
          total: orderJson.total
        }

        this.orders = orderJson.data.map((order, index) => {
          return {
            ...order,
            _changes: {},
            _errors: {},
            _index: index,
            _expanded: false,
            _clientName: this.clients.find(client => client.id === order.client_id)?.name ?? '',
            _assignedUserName: this.users.find(user => user.id === order.assigned_user_id)?.name ?? ''
          };
        });
      },
      updateOrder(order) {
        this.orders[order._index] = order;
      },
      resetExpansions() {
        this.orders.forEach(order => {
          order._expandedAssignedUserDropdown = false;
        });
      },
      goToPage(page) {
        this.fetchOrderData(this.searchTerm, this.filterTerm, this.sortOrder, page);
      },
      sort(sortOrder) {
        this.sortOrder = sortOrder;
        this.fetchOrderData(this.searchTerm, this.filterTerm, this.sortOrder, this.ordersMeta.currentPage);
      },
      searchInput(event) {
        this.searchText = event.target.value;
      },
      deleteSearchTerm() {
        this.searchText = '';
        this.search();
      },
      search: async function () {
        this.searchTerm = this.searchText;
        this.fetchOrderData(this.searchTerm, this.filterTerm, this.sortOrder, '1');
      }
    }
  }
</script>
